
import { useState, useEffect } from 'react';

interface LeadCaptureModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function LeadCaptureModal({ isOpen, onClose }: LeadCaptureModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    fleet_size: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d66as5k8e72clamqhcg0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setTimeout(() => {
          window.open(
            `https://wa.me/5511999999999?text=Olá,%20sou%20${encodeURIComponent(formData.name)}%20da%20empresa%20${encodeURIComponent(formData.company)}.%20Gostaria%20de%20conhecer%20o%20TG%20WEB!`,
            '_blank'
          );
          onClose();
          setFormData({ name: '', email: '', phone: '', company: '', fleet_size: '' });
          setSubmitStatus('idle');
        }, 1500);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        <div className="relative p-5 sm:p-6 lg:p-8">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 sm:top-4 sm:right-4 w-8 h-8 flex items-center justify-center text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>

          <div className="text-center mb-5 sm:mb-6">
            <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center bg-secondary/10 rounded-full mx-auto mb-3 sm:mb-4">
              <i className="ri-rocket-line text-2xl sm:text-3xl text-secondary"></i>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-neutral-900 mb-1 sm:mb-2">
              Transforme sua Transportadora
            </h3>
            <p className="text-neutral-600 text-xs sm:text-sm">
              Preencha o formulário e receba uma demonstração personalizada
            </p>
          </div>

          {submitStatus === 'success' ? (
            <div className="text-center py-6 sm:py-8">
              <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-3 sm:mb-4">
                <i className="ri-check-line text-2xl sm:text-3xl text-green-600"></i>
              </div>
              <h4 className="text-lg sm:text-xl font-semibold text-neutral-900 mb-2">Enviado com sucesso!</h4>
              <p className="text-neutral-600 text-xs sm:text-sm">Redirecionando para o WhatsApp...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-readdy-form id="lead-capture-form">
              <div className="space-y-3 sm:space-y-4">
                <div>
                  <label htmlFor="name" className="block text-xs sm:text-sm font-medium text-neutral-700 mb-1">
                    Nome completo *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                    placeholder="Seu nome"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-neutral-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                    placeholder="seu@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-xs sm:text-sm font-medium text-neutral-700 mb-1">
                    Telefone *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                    placeholder="(11) 99999-9999"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-xs sm:text-sm font-medium text-neutral-700 mb-1">
                    Nome da Transportadora *
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    required
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                    placeholder="Nome da empresa"
                  />
                </div>

                <div>
                  <label htmlFor="fleet_size" className="block text-xs sm:text-sm font-medium text-neutral-700 mb-1">
                    Tamanho da Frota
                  </label>
                  <select
                    id="fleet_size"
                    name="fleet_size"
                    value={formData.fleet_size}
                    onChange={(e) => setFormData({ ...formData, fleet_size: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                  >
                    <option value="">Selecione</option>
                    <option value="1-5">1 a 5 veículos</option>
                    <option value="6-10">6 a 10 veículos</option>
                    <option value="11-20">11 a 20 veículos</option>
                    <option value="21-50">21 a 50 veículos</option>
                    <option value="50+">Mais de 50 veículos</option>
                  </select>
                </div>
              </div>

              {submitStatus === 'error' && (
                <div className="mt-3 sm:mt-4 p-2.5 sm:p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-xs sm:text-sm text-red-600">Erro ao enviar. Tente novamente.</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full mt-5 sm:mt-6 px-6 py-2.5 sm:py-3 bg-secondary text-white font-semibold rounded-lg hover:bg-secondary-600 transition-all shadow-soft hover:shadow-hover disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer text-sm sm:text-base"
              >
                {isSubmitting ? 'Enviando...' : 'Solicitar Demonstração'}
              </button>

              <p className="text-[10px] sm:text-xs text-neutral-500 text-center mt-3 sm:mt-4">
                Ao enviar, você concorda com nossa Política de Privacidade
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
